import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Card } from "../ui";
import type { ProvinceDTO } from "../../api/contract";
import {
  ApiRequestError,
  catastoByPoint,
  geocodeAddress,
  reverseGeocode,
  type ByPointResponse,
  type GeocodeHit,
  type ParcelDTO,
} from "../api";
import { AddressSearchCard } from "./address-lookup/AddressSearchCard";
import { buildScrapeEstimate, getProvinceForHit } from "./address-lookup/estimate";
import { EstimateCard } from "./address-lookup/EstimateCard";
import {
  configureLeafletIcon,
  drawParcelLayer,
  ITALY_CENTER,
} from "./address-lookup/map-utils";
import { SelectedPointCard } from "./address-lookup/SelectedPointCard";

configureLeafletIcon();

export function AddressLookup({
  provinces,
  pushToast,
}: {
  provinces: ProvinceDTO[];
  pushToast: (kind: "ok" | "err", msg: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<GeocodeHit[]>([]);
  const [showSug, setShowSug] = useState(false);
  const [selected, setSelected] = useState<GeocodeHit | null>(null);
  const [parcels, setParcels] = useState<ParcelDTO[] | null>(null);
  const [parcelElapsed, setParcelElapsed] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const mapHostRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const parcelLayerRef = useRef<L.LayerGroup | null>(null);

  // ── Map init ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapHostRef.current || mapRef.current) return;
    const map = L.map(mapHostRef.current, {
      center: ITALY_CENTER,
      zoom: 6,
      zoomControl: true,
    });
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap",
      maxZoom: 19,
    }).addTo(map);
    map.on("click", (e: L.LeafletMouseEvent) => {
      void handlePick(e.latlng.lat, e.latlng.lng, "map");
    });
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recenter + marker when selected changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selected) return;
    const pos: L.LatLngExpression = [selected.lat, selected.lon];
    map.flyTo(pos, 18, { duration: 0.6 });
    if (markerRef.current) markerRef.current.setLatLng(pos);
    else markerRef.current = L.marker(pos).addTo(map);
  }, [selected]);

  // Disegna poligoni particelle catastali sulla mappa
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (parcelLayerRef.current) {
      parcelLayerRef.current.remove();
      parcelLayerRef.current = null;
    }
    if (!parcels || parcels.length === 0) return;
    parcelLayerRef.current = drawParcelLayer(map, parcels);
  }, [parcels]);

  // ── Autocomplete ────────────────────────────────────────────────────
  useEffect(() => {
    if (query.trim().length < 4) {
      setSuggestions([]);
      return;
    }
    const h = setTimeout(async () => {
      try {
        const res = await geocodeAddress(query.trim(), 5);
        setSuggestions(res.hits);
        setShowSug(true);
      } catch {
        /* silent */
      }
    }, 500);
    return () => clearTimeout(h);
  }, [query]);

  async function handlePick(lat: number, lon: number, origin: "map" | "suggest" | "search") {
    setBusy(true);
    setErr(null);
    setParcels(null);
    setParcelElapsed(null);
    try {
      let hit: GeocodeHit;
      if (origin === "map") {
        const rev = await reverseGeocode(lat, lon);
        hit = rev;
      } else {
        // quando arriva da suggest/search abbiamo già la hit completa
        hit = { lat, lon, display_name: `${lat.toFixed(5)}, ${lon.toFixed(5)}` } as GeocodeHit;
      }
      setSelected(hit);
      if (origin !== "search") setQuery(hit.display_name);
      setShowSug(false);

      // Parallelo: lookup particelle catastali
      const pt: ByPointResponse = await catastoByPoint(lat, lon);
      setParcels(pt.matches);
      setParcelElapsed(pt.elapsed_ms);
    } catch (e) {
      setErr(e instanceof ApiRequestError ? e.message : "Errore lookup");
    } finally {
      setBusy(false);
    }
  }

  function pickSuggestion(s: GeocodeHit) {
    setSelected(s);
    setQuery(s.display_name);
    setShowSug(false);
    setBusy(true);
    // lookup particelle per il punto suggerito
    catastoByPoint(s.lat, s.lon)
      .then((pt) => {
        setParcels(pt.matches);
        setParcelElapsed(pt.elapsed_ms);
      })
      .catch((e) => setErr(e instanceof ApiRequestError ? e.message : "Errore lookup"))
      .finally(() => setBusy(false));
  }

  async function handleSearch() {
    if (!query.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await geocodeAddress(query.trim(), 1);
      if (res.hits.length === 0) {
        setErr("Nessun risultato");
        return;
      }
      pickSuggestion(res.hits[0]);
    } catch (e) {
      setErr(e instanceof ApiRequestError ? e.message : "Errore geocoding");
    } finally {
      setBusy(false);
    }
  }

  // ── Stima scrape ─────────────────────────────────────────────────────
  const province = useMemo(() => {
    return getProvinceForHit(selected, provinces);
  }, [selected, provinces]);

  const estimate = useMemo(() => {
    if (!province) return null;
    return buildScrapeEstimate(province);
  }, [province]);

  function copyCoordinates() {
    if (navigator.clipboard && selected) {
      void navigator.clipboard.writeText(
        `${selected.lat.toFixed(6)}, ${selected.lon.toFixed(6)}`,
      );
      pushToast("ok", "Coordinate copiate");
    }
  }

  return (
    <div className="col gap-16">
      <AddressSearchCard
        query={query}
        suggestions={suggestions}
        showSuggestions={showSug}
        busy={busy}
        error={err}
        onQueryChange={setQuery}
        onSearch={() => void handleSearch()}
        onPickSuggestion={pickSuggestion}
        onShowSuggestions={() => setShowSug(true)}
        onHideSuggestions={() => setShowSug(false)}
      />

      <div className="grid address-layout">
        <Card
          title="Mappa"
          sub="OpenStreetMap · click per selezionare un punto"
          noPad
        >
          <div
            ref={mapHostRef}
            style={{
              width: "100%",
              height: 560,
              borderRadius: "0 0 10px 10px",
              overflow: "hidden",
            }}
          />
        </Card>

        <div className="col gap-16">
          <SelectedPointCard
            selected={selected}
            parcels={parcels}
            parcelElapsed={parcelElapsed}
          />

          {selected && (
            <EstimateCard
              selected={selected}
              province={province}
              estimate={estimate}
              onCopyCoordinates={copyCoordinates}
            />
          )}
        </div>
      </div>
    </div>
  );
}
